import type MockAdapter from 'axios-mock-adapter'
import { auditFieldsForCreate, newId } from '../engine/context'
import { insertRow, readTable } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import { fail, handle, parse } from './c07.shared'
import {
  type ImportCheck,
  type ImportRow,
  type ImportRowsBody,
  importFileOf,
  importRowsOf,
  importSummary,
  masterLog,
  requireWrite,
  text,
  up
} from './masterData.shared'

/**
 * Import Excel Master Raw Material (MST-02, BA 12A JAL-PG-40-006) dan Master Supplier (MST-04, JAL-PG-40-009):
 * `/api/v1/wms/master-material/import-{preview,commit}` dan `/api/v1/wms/master-supplier/import-{preview,commit}`.
 * Simpan manual tetap lewat Directus `/items/mt_material` & `/items/mt_companies`; import memakai aturan yang sama dan
 * memvalidasi ulang di server pada pratinjau MAUPUN konfirmasi (peramban hanya mengurai berkas .xlsx).
 *
 * Aturan MST-02 (prototype v7.50): kode manual 2–16 huruf/angka/strip `^[A-Z0-9-]{2,16}$`, kategori & satuan harus
 * ada di master, isi per satuan beli > 0, kapasitas/hari dan Reorder Point angka bulat atau kosong.
 * Aturan MST-04: kode + nama + material dipasok; kode material harus ada di MST-02 ("Material … tak dikenal").
 * Hak tulis = capability platform (`WRITE_ROLES`): MST-02 Admin WH RM; MST-04 Admin WH RM + Purchasing (v7.50).
 */

const MATERIAL_PAGE = 'MST-02'
const SUPPLIER_PAGE = 'MST-04'
const MATERIAL_WRITE_MESSAGE = 'Import material hanya untuk Admin WH RM.'
const SUPPLIER_WRITE_MESSAGE = 'Import supplier hanya untuk Admin WH RM dan Purchasing.'

export const MATERIAL_IMPORT_FILE = 'Master Inventory Raw Material (update).xlsx'
/** Judul kolom templat (sama dengan `RAW_MATERIAL_IMPORT` di service-library; dicek oleh uji). */
export const MATERIAL_IMPORT_HEADERS = [
  'Kode Material',
  'Nama Material',
  'Kategori',
  'Satuan Beli',
  'Isi per Satuan Beli',
  'Unit Kecil',
  'Kapasitas/Hari',
  'Reorder Point'
]
export const SUPPLIER_IMPORT_FILE = 'Master Supplier (update).xlsx'
export const SUPPLIER_IMPORT_HEADERS = ['Kode Supplier', 'Nama Supplier', 'Material Dipasok']

/** Kode material v7.50 (`C.kodeMatSah`). */
export const MATERIAL_CODE = /^[A-Z0-9-]{2,16}$/
const SUPPLIER_CODE = /^[A-Z0-9_-]{2,50}$/
const INTEGER = /^\d+$/
const NAME_MAX = 150
const SUPPLIER_NAME_MAX = 255

const idOf = (value: unknown) =>
  typeof value === 'object' && value !== null ? String((value as { id?: unknown }).id ?? '') : String(value ?? '')
const same = (a: unknown, b: unknown) => text(a).toLowerCase() === text(b).toLowerCase()

/** Cari baris referensi menurut kode ATAU nama (tanpa beda huruf besar/kecil). */
const lookup = (collection: string, value: string, kind?: string): LocalRow | undefined =>
  readTable(collection).find(row => (!kind || row.kind === kind) && (same(row.code, value) || same(row.name, value)))

/* ------------------------------------------------------------------ Master Raw Material */

type MaterialColumns = {
  code: string
  name: string
  category_id: string
  default_purchase_unit_id: string
  default_purchase_unit_qty: number
  unit_small_id: string
  cap_day: number | null
  rop_manual: number | null
}
type MaterialVerdict = ImportCheck & { row?: MaterialColumns }

function materialVerdict(raw: Record<string, string>, seen: Set<string>): MaterialVerdict {
  const code = up(raw['Kode Material'])
  if (!code) return { state: 'invalid', reason: 'Kode Material wajib diisi' }
  if (!MATERIAL_CODE.test(code)) return { state: 'invalid', reason: 'Kode 2–16 huruf/angka/tanda minus, tanpa spasi' }
  const again = seen.has(code)
  seen.add(code)
  if (again) return { state: 'duplicate', reason: 'Kode dobel di berkas' }
  const name = text(raw['Nama Material'])
  const existing = readTable('mt_material').find(row => up(row.code) === code)
  if (existing)
    return same(existing.name, name)
      ? { state: 'duplicate', reason: 'Sudah ada' }
      : { state: 'invalid', reason: `Kode ${code} dipakai ${text(existing.name)}` }
  if (!name) return { state: 'invalid', reason: 'Nama Material wajib diisi' }
  if (name.length > NAME_MAX) return { state: 'invalid', reason: `Nama Material maksimal ${NAME_MAX} karakter` }
  const categoryRaw = text(raw.Kategori)
  if (!categoryRaw) return { state: 'invalid', reason: 'Kategori wajib diisi' }
  const category = lookup('mt_material_group', categoryRaw)
  if (!category) return { state: 'invalid', reason: `Kategori ${categoryRaw} tak dikenal` }
  const bigRaw = text(raw['Satuan Beli'])
  if (!bigRaw) return { state: 'invalid', reason: 'Satuan Beli wajib diisi' }
  const unitBig = lookup('mt_unit', bigRaw, 'PACK')
  if (!unitBig) return { state: 'invalid', reason: `Satuan Beli ${bigRaw} tak dikenal` }
  const perBig = Number(text(raw['Isi per Satuan Beli']).replace(',', '.'))
  if (!(perBig > 0)) return { state: 'invalid', reason: 'Isi per satuan beli harus lebih dari 0' }
  const smallRaw = text(raw['Unit Kecil'])
  if (!smallRaw) return { state: 'invalid', reason: 'Unit Kecil wajib diisi' }
  const unitSmall = lookup('mt_unit', smallRaw, 'SMALL')
  if (!unitSmall) return { state: 'invalid', reason: `Unit Kecil ${smallRaw} tak dikenal` }
  const optionalInteger = (value: unknown, label: string): { value: number | null; error?: string } => {
    const cell = text(value)
    if (!cell) return { value: null }
    return INTEGER.test(cell)
      ? { value: Number(cell) }
      : { value: null, error: `${label} harus angka bulat, atau kosong` }
  }
  const cap = optionalInteger(raw['Kapasitas/Hari'], 'Kapasitas/Hari')
  if (cap.error) return { state: 'invalid', reason: cap.error }
  const rop = optionalInteger(raw['Reorder Point'], 'Reorder Point')
  if (rop.error) return { state: 'invalid', reason: rop.error }
  return {
    state: 'valid',
    row: {
      code,
      name,
      category_id: idOf(category),
      default_purchase_unit_id: idOf(unitBig),
      default_purchase_unit_qty: perBig,
      unit_small_id: idOf(unitSmall),
      cap_day: cap.value,
      rop_manual: rop.value
    }
  }
}

export function materialImportPreview(body: ImportRowsBody) {
  requireWrite(MATERIAL_PAGE, MATERIAL_WRITE_MESSAGE)
  const seen = new Set<string>()
  const rows: ImportRow[] = importRowsOf(body).map(({ n, raw }) => ({
    n,
    cells: [
      up(raw['Kode Material']),
      raw['Nama Material'],
      raw.Kategori,
      raw['Satuan Beli'] && raw['Isi per Satuan Beli']
        ? `${raw['Satuan Beli']} (${raw['Isi per Satuan Beli']} ${raw['Unit Kecil'] ?? ''})`.trim()
        : (raw['Satuan Beli'] ?? ''),
      raw['Kapasitas/Hari'] || '—',
      raw['Reorder Point'] || 'usulan sistem'
    ],
    ...materialVerdict(raw, seen)
  }))
  return importSummary(importFileOf(body, MATERIAL_IMPORT_FILE), rows)
}

export function materialImportCommit(body: ImportRowsBody) {
  requireWrite(MATERIAL_PAGE, MATERIAL_WRITE_MESSAGE)
  const seen = new Set<string>()
  let imported = 0
  let skipped = 0
  let rejected = 0
  importRowsOf(body).forEach(({ raw }) => {
    const verdict = materialVerdict(raw, seen)
    if (verdict.state === 'duplicate') skipped += 1
    else if (verdict.state === 'invalid' || !verdict.row) rejected += 1
    else {
      insertRow('mt_material', {
        ...auditFieldsForCreate(),
        id: newId(),
        ...verdict.row,
        variant: null,
        is_active: true
      })
      imported += 1
    }
  })
  if (!imported) fail('Tidak ada baris valid untuk diimport.')
  masterLog(
    'RM',
    MATERIAL_PAGE,
    'mst-import',
    importFileOf(body, MATERIAL_IMPORT_FILE),
    `Import ${imported} material (dilewati ${skipped} duplikat, ditolak ${rejected})`
  )
  return { imported, skipped, rejected }
}

/* ------------------------------------------------------------------ Master Supplier */

/** "ETK-A; ETK-B, ETK-A" → kode unik, huruf besar, urutan asli. */
export const parseMaterialCodes = (value: unknown): string[] => [
  ...new Set(
    text(value)
      .split(/[,;\n]/)
      .map(item => item.trim().toUpperCase())
      .filter(Boolean)
  )
]

type SupplierVerdict = ImportCheck & { row?: { code: string; name: string; mats: string[] } }

function supplierVerdict(raw: Record<string, string>, seen: Set<string>): SupplierVerdict {
  const code = up(raw['Kode Supplier'])
  if (!code) return { state: 'invalid', reason: 'Kode Supplier wajib diisi' }
  if (!SUPPLIER_CODE.test(code))
    return { state: 'invalid', reason: 'Kode 2–50 huruf/angka/strip/underscore, tanpa spasi' }
  const again = seen.has(code)
  seen.add(code)
  if (again) return { state: 'duplicate', reason: 'Kode dobel di berkas' }
  const name = text(raw['Nama Supplier'])
  const existing = readTable('mt_companies').find(row => up(row.code) === code)
  if (existing) {
    if (existing.type !== 'supplier') return { state: 'invalid', reason: `Kode ${code} dipakai ${text(existing.name)}` }
    return same(existing.name, name)
      ? { state: 'duplicate', reason: 'Sudah ada' }
      : { state: 'invalid', reason: `Kode ${code} dipakai ${text(existing.name)}` }
  }
  if (!name) return { state: 'invalid', reason: 'Nama Supplier wajib diisi' }
  if (name.length > SUPPLIER_NAME_MAX)
    return { state: 'invalid', reason: `Nama Supplier maksimal ${SUPPLIER_NAME_MAX} karakter` }
  const mats = parseMaterialCodes(raw['Material Dipasok'])
  if (!mats.length) return { state: 'invalid', reason: 'Minimal satu material dipasok' }
  const known = new Set(readTable('mt_material').map(row => up(row.code)))
  const unknown = mats.filter(item => !known.has(item))
  if (unknown.length) return { state: 'invalid', reason: `Material ${unknown.join(', ')} tak dikenal` }
  return { state: 'valid', row: { code, name, mats } }
}

export function supplierImportPreview(body: ImportRowsBody) {
  requireWrite(SUPPLIER_PAGE, SUPPLIER_WRITE_MESSAGE)
  const seen = new Set<string>()
  const rows: ImportRow[] = importRowsOf(body).map(({ n, raw }) => ({
    n,
    cells: [up(raw['Kode Supplier']), raw['Nama Supplier'], parseMaterialCodes(raw['Material Dipasok']).join(', ')],
    ...supplierVerdict(raw, seen)
  }))
  return importSummary(importFileOf(body, SUPPLIER_IMPORT_FILE), rows)
}

export function supplierImportCommit(body: ImportRowsBody) {
  requireWrite(SUPPLIER_PAGE, SUPPLIER_WRITE_MESSAGE)
  const seen = new Set<string>()
  let imported = 0
  let skipped = 0
  let rejected = 0
  importRowsOf(body).forEach(({ raw }) => {
    const verdict = supplierVerdict(raw, seen)
    if (verdict.state === 'duplicate') skipped += 1
    else if (verdict.state === 'invalid' || !verdict.row) rejected += 1
    else {
      insertRow('mt_companies', {
        ...auditFieldsForCreate(),
        id: newId(),
        code: verdict.row.code,
        name: verdict.row.name,
        type: 'supplier',
        /** Sama dengan simpan manual di layar Master Supplier: material dipasok disimpan sebagai JSON `{ mats }`. */
        description: JSON.stringify({ mats: verdict.row.mats }),
        city: null,
        address: null,
        phone: null,
        is_active: true,
        status: 'published'
      })
      imported += 1
    }
  })
  if (!imported) fail('Tidak ada baris valid untuk diimport.')
  masterLog(
    'RM',
    SUPPLIER_PAGE,
    'mst-import',
    importFileOf(body, SUPPLIER_IMPORT_FILE),
    `Import ${imported} supplier (dilewati ${skipped} duplikat, ditolak ${rejected})`
  )
  return { imported, skipped, rejected }
}

export function registerMasterMaterialHandlers(mock: MockAdapter) {
  const post = (url: string, run: (body: ImportRowsBody) => unknown) =>
    mock.onPost(url).reply(config => handle(() => run(parse<ImportRowsBody>(config.data))))
  post('/api/v1/wms/master-material/import-preview', materialImportPreview)
  post('/api/v1/wms/master-material/import-commit', materialImportCommit)
  post('/api/v1/wms/master-supplier/import-preview', supplierImportPreview)
  post('/api/v1/wms/master-supplier/import-commit', supplierImportCommit)
}
